import { ReactComponent as DefaultPropertyImageSvg } from 'assets/svg/Default-Property-Image.svg';
import { ReactComponent as SeeDetailsMobileSvg } from 'assets/svg/Property-See-Details-Mobile.svg';
import { ReactComponent as PropertyLockedSvg } from 'assets/svg/PropertyLocked.svg';

import NewLabel from 'components/Labels/NewCompetitor';
import { OneLetterAvatar } from 'components/LetterAvatar';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { convertDateIntoLocaleString, formatNumber, GetCountryCode, isNew } from 'util/Funcs';
import styles from './propertytable.module.css';
import { Button, Menu } from '@mui/material';
import { PropertyDTO } from 'util/Types';
import { useModals } from 'pages/MainProvider';

interface PropertyTableProps {
    propertyList: PropertyDTO[];
    lockedTitle: string;
    shouldShowDetail: boolean;
}

const fakeProperty: PropertyDTO = {
    propertyId: 'b8441c02-3d52-480f-824a-baf6e85243c7',
    companyId: '006ffd97-16c2-4529-99ea-702b3b9d1376',
    companyName: 'Fincas Fiol',
    createdAt: new Date(''),
    onOff: true,
    title: 'Impressive Property for Sale with Vacation License for 10 People in Puig de Ros',
    description:
        "We present this excellent property, which has a vacation license for 10 people (old license, so it does not require renewal), ideal for those looking for a profitable investment or a cozy home in a privileged environment. Upon entering, you are welcomed by an attractive foyer that leads to spacious rooms, where all spaces benefit from high ceilings with exposed beams and charming sloping roofs, depending on the room. On the ground floor, you will find a magnificent designer kitchen made of solid iroko wood and silestone, equipped with a great range of appliances, including a 5-burner stove and an office area. This floor also houses a spacious living-dining room with a fireplace, a full bathroom, and a double bedroom. It is worth noting that from all rooms on this floor, there is direct access to the beautiful garden, porches, and impressive swimming pool. Accessing the first floor via a majestic staircase, there are 3 double bedrooms, a bathroom, and a master room with an en-suite bathroom. All bedrooms feature spacious fitted wardrobes, and 4 of them have direct access to terraces or the garden. The master bedroom, with an en-suite bathroom, is located in an independent structure within the estate. The property offers comfort year-round, thanks to ducted air conditioning for both cold and heat and the pre-installation of heating. The generously sized pool includes a jacuzzi, a zone for balinese beds, lawn/grass, and a terrace with a porch. The pool is equipped with an outdoor shower with hot/cold water and a barbecue area, creating an ideal space for enjoying outdoor moments. The floors on the ground floor are made of Porcelanosa, while solid wood floors are used on the first floor. The solid interior carpentry is made of iroko, offering a warm and cozy finish. The closures are made of climalit aluminum, and the shutters are also aluminum, ensuring comfort and energy efficiency. From the first floor and the attic, you can enjoy views of the sea and the Bay of Palma. The water system uses a butane gas heater, and the property includes a charming turret with five windows that offer unobstructed views. From here, you access a solarium terrace of approximately 10 m², ideal for relaxing and appreciating the surroundings. The garden is meticulously maintained, with a variety of plants such as roses, cacti, callas, benjamin figs, grass, olive trees, yuccas, and pink bougainvilleas, all irrigated by an automatic sprinkler system. Don't miss the opportunity to acquire this magnificent property that combines style, comfort, and an enviable location! In addition to a business opportunity with the profitability of vacation rentals.",
    webPageUrl: 'https://fincasfiol.com/propiedad/impresionante-propiedad-en-venta-con-licencia-vacacional-para-10-plazas-en-puig-de-ros-22407261/',
    webPageId: '8ac3ef63-2210-4880-93c1-dba06af9b3c1',
    latitude: 39.44876,
    longitude: 2.74937,
    address: {
        StreetAddress: 'Calle de Falzia, 52',
        City: 'Lluchmayor',
        State: 'Balearic Islands',
        Country: 'Spain',
        Neighborhood: 'Urbanización sa Moreria',
    },
    details: {
        entityId: 'f5a7bb8e-6a6b-47d8-a3a4-e7fa1c8957f7',
        propertyType: 'Villa',
        price: 1710000,
        currency: 'EUR',
        listingType: 'sale',
        bedrooms: 5,
        bathrooms: 4,
        sizeLand: 260,
        sizeLiving: 240,
        sizePlot: 605,
        features: [
            'All exterior',
            'Appliances',
            'Armored door',
            'Automatic doors',
            'Automatic irrigation',
            'Balcony',
            'Barbecue',
            'Electricity',
            'Fireplace',
            'Fitted wardrobes',
            'Garden',
            'Jacuzzi',
            'Pergola',
            'Separate apartment',
            'Solarium',
            'Storage room',
            'Terrace',
            'TV',
            'Water',
            'Water tank',
        ],
        availability: 'Immediate',
        amenities: ['Pool', 'Garden', 'Terrace', 'Parking', 'Security System'],
        energyCertRating: 'A',
        hasPool: true,
        poolSize: '8x4 meters',
        furnished: true,
        needsRenovation: false,
        floorsCount: 2,
        floorNumber: 0,
        totalFloors: 2,
        elevator: false,
        kitchen: true,
        kitchenType: 'Open Plan',
        livingAreaExternal: 50,
        gardenDescription: 'Beautiful landscaped garden with automatic irrigation system',
        gardenSize: 300,
        parkingTypes: ['Garage', 'Street'],
        parkingMonthlyCosts: ['200', '0'],
        terraceTypes: ['Private', 'Communal'],
        terraceSizes: ['30', '50'],
        constructionYear: 2015,
        energyCertAvailable: true,
        pricePerSquareMeter: 7125,
        heatingType: 'Central',
        hasAC: true,
        hasSolar: true,
        hasElectricityLine: true,
        underConstruction: false,
    },
    imageUrls: ['https://fotos15.apinmo.com/3987/22407261/4-1.jpg', 'https://fotos15.apinmo.com/3987/22407261/4-2.jpg'],
};

const HeaderItem = ({ className = '', title }: { className?: string; title: string }) => {
    return (
        <div className={`flex items-center gap-[2px] cursor-pointer ${className}`}>
            <span className={styles['headertitle']}>{title}</span>
        </div>
    );
};

const MIN_W = 400;
const MIN_H = 200;

const DateItem = ({ property }: { property: PropertyDTO }) => {
    const isNewProperty: boolean = isNew(property.createdAt);
    const [image, setImage] = useState<string>('');

    useEffect(() => {
        if (!property.imageUrls) {
            setImage('');
            return;
        }

        const filterImages = async () => {
            for (const imgurl of property.imageUrls) {
                const img = new window.Image();
                img.src = imgurl;

                const isValid = await new Promise<boolean>((resolve) => {
                    img.onload = () => {
                        resolve(img.width >= MIN_W && img.height >= MIN_H);
                    };
                    img.onerror = () => resolve(false);
                });

                if (isValid) {
                    setImage(imgurl);
                    break;
                }
            }
        };

        filterImages();
    }, [property.imageUrls]);

    return (
        <div className="relative flex gap-[10px] overflow-hidden">
            {isNewProperty ? (
                <NewLabel />
            ) : (
                <div
                    className="min-w-[42px] w-[42px] h-[42px] border border-my-gray border-opacity-60 rounded-[5px] overflow-hidden flex items-center justify-center"
                    data-cy="property-image"
                >
                    {image ? <img src={image} width={42} height={42} /> : <DefaultPropertyImageSvg />}
                </div>
            )}

            <div className="flex flex-col justify-between">
                <div className="font-medium truncate">
                    <span data-cy="property-title">{property.title}</span>
                    <div className="z-[2] absolute right-0 top-[50%] h-[600%] w-[80%] bg-white blur-[50px] translate-x-[50%] translate-y-[-50%]"></div>
                </div>
                <div className="text-[12px]" data-cy="property-date">
                    {convertDateIntoLocaleString(property.createdAt)}
                </div>
            </div>
        </div>
    );
};

const UnsavedCompetitorPropertyTable = ({ propertyList, lockedTitle, shouldShowDetail }: PropertyTableProps) => {
    const { showPropertyDetailModal, showAddtionalPropertyDetail, isAdditionalDetailOpened, selectedProperty } = useModals();

    const onClickShowDetail = (property: PropertyDTO) => {
        showPropertyDetailModal(property, true);
    };

    const openAdditionalPanelClicked = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>, p: PropertyDTO) => {
        showAddtionalPropertyDetail(evt.currentTarget, p);
    };

    return (
        <>
            <div className={`w-full font-medium ${styles['propertytable']}`}>
                {/* header */}
                <div
                    className={`${styles['tr']} ${styles['thead']} grid xl:grid-cols-10 grid-cols-9 items-center text-my-black text-opacity-60 border-y border-my-gray border-opacity-40 py-[15px] px-[10px] mb-[12px] bg-my-gray bg-opacity-20`}
                >
                    <HeaderItem title="Listing Date" className="col-span-2" />
                    <HeaderItem title="Competitor" />
                    <HeaderItem title="Country" />
                    <HeaderItem title="City" />
                    <HeaderItem title="Property Type" />
                    <HeaderItem title="Price" />
                    <HeaderItem title="Living Size" />
                    <HeaderItem title="Plot area" className={`xl:flex hidden xl:col-span-2`} />
                </div>

                <div className={`flex flex-col gap-[4px] text-opacity-80 ${styles['body']}`} data-cy="property-list-container">
                    {propertyList.slice(0, 2).map((property: PropertyDTO, index: number) => {
                        return (
                            <div key={index} className={`${styles['tr']} grid xl:grid-cols-10 grid-cols-9 items-center border-opacity-40 py-[8px] px-[10px]`}>
                                <div className="col-span-2 pr-[12x]">
                                    <DateItem property={property} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-[5px]">
                                        <OneLetterAvatar
                                            title={property.details.listingType || 'Sales'}
                                            colorClass={property.details.listingType === 'rental' ? 'text-my-green' : 'text-my-blue'}
                                        />
                                        <span data-cy="property-company">{property.companyName || ''}</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-[5px]">
                                        {property.address.Country && (
                                            <ReactCountryFlag
                                                svg
                                                countryCode={GetCountryCode(property.address.Country || '') || ''}
                                                style={{
                                                    width: '1.5em',
                                                    height: '1.5em',
                                                }}
                                            />
                                        )}
                                        <span data-cy="property-country">{property.address.Country}</span>
                                    </div>
                                </div>
                                <div data-cy="property-city">{`${property.address.City || ''}`}</div>
                                <div data-cy="property-type">{`${property.details.propertyType || ''}`}</div>
                                <div data-cy="property-price">
                                    {property.details.price && property.details.price > 0
                                        ? `${formatNumber(property.details.price)} ${getSymbolFromCurrency(property.details.currency) || ''}`
                                        : 'On Request'}
                                </div>
                                <div data-cy="property-constructed-area">{`${formatNumber(property.details.sizeLand || 0)} m²`}</div>
                                <div className={`flex xl:justify-between justify-end items-center xl:col-span-2`}>
                                    <div className="xl:inline-block hidden" data-cy="property-plot-area">
                                        {`${formatNumber(property.details.sizePlot || 0)} m²`}
                                    </div>

                                    <div className="flex items-center text-my-blue justify-end">
                                        {shouldShowDetail && (
                                            <>
                                                <div className="lg:flex hidden gap-[12px] items-center">
                                                    <Button variant="outlined" onClick={() => onClickShowDetail(property)} data-cy="property-detail-button">
                                                        Details
                                                    </Button>
                                                </div>

                                                <div
                                                    className={`lg:hidden w-[50px] h-[48px] flex items-center justify-center border ${isAdditionalDetailOpened && property.webPageId === selectedProperty?.webPageId ? 'bg-my-black text-white' : 'border-my-blue text-my-blue'} cursor-pointer`}
                                                    onClick={(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => openAdditionalPanelClicked(ev, property)}
                                                >
                                                    <SeeDetailsMobileSvg
                                                        className={
                                                            isAdditionalDetailOpened && property.webPageId === selectedProperty?.webPageId ? 'rotate-180' : ''
                                                        }
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div className="relative">
                        <div className="absolute w-full h-full top-0 left-0 bg-white z-10 bg-opacity-30 flex flex-col items-center justify-center gap-[12px]">
                            <PropertyLockedSvg />
                            <div className="font-medium text-[12px]">{lockedTitle}</div>
                        </div>
                        <div className="blur-[3px]">
                            {[fakeProperty, fakeProperty, fakeProperty].map((property: PropertyDTO, index: number) => {
                                return (
                                    <div
                                        key={index}
                                        className={`${styles['tr']} grid xl:grid-cols-10 grid-cols-9 items-center border-opacity-40 py-[8px] px-[10px]`}
                                    >
                                        <div className="col-span-2 pr-[12x]">
                                            <DateItem property={property} />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-[5px]">
                                                <OneLetterAvatar
                                                    title={property.details.listingType || 'Sales'}
                                                    colorClass={property.details.listingType === 'rental' ? 'text-my-green' : 'text-my-blue'}
                                                />
                                                <span data-cy="property-company">{property.companyName || ''}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-[5px]">
                                                {property.address.Country && (
                                                    <ReactCountryFlag
                                                        svg
                                                        countryCode={GetCountryCode(property.address.Country || '') || ''}
                                                        style={{
                                                            width: '1.5em',
                                                            height: '1.5em',
                                                        }}
                                                    />
                                                )}
                                                <span data-cy="property-country">{property.address.Country}</span>
                                            </div>
                                        </div>
                                        <div data-cy="property-city">{`${property.address.City || ''}`}</div>
                                        <div data-cy="property-type">{`${property.details.propertyType || ''}`}</div>
                                        <div data-cy="property-price">
                                            {property.details.price && property.details.price > 0
                                                ? `${formatNumber(property.details.price)} ${getSymbolFromCurrency(property.details.currency) || ''}`
                                                : 'On Request'}
                                        </div>
                                        <div data-cy="property-constructed-area">{`${formatNumber(property.details.sizeLand || 0)} m²`}</div>
                                        <div className={`flex xl:justify-between justify-end items-center xl:col-span-2`}>
                                            <div className="xl:inline-block hidden" data-cy="property-plot-area">
                                                {`${formatNumber(property.details.sizePlot || 0)} m²`}
                                            </div>

                                            <div className="flex items-center text-my-blue justify-end">
                                                <div className="lg:flex hidden gap-[12px] items-center">
                                                    <Button variant="outlined" onClick={() => onClickShowDetail(property)} data-cy="property-detail-button">
                                                        Details
                                                    </Button>
                                                </div>

                                                <div
                                                    className={`lg:hidden w-[50px] h-[48px] flex items-center justify-center border ${isAdditionalDetailOpened && property.webPageId === selectedProperty?.webPageId ? 'bg-my-black text-white' : 'border-my-blue text-my-blue'} cursor-pointer`}
                                                    onClick={(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => openAdditionalPanelClicked(ev, property)}
                                                >
                                                    <SeeDetailsMobileSvg
                                                        className={
                                                            isAdditionalDetailOpened && property.webPageId === selectedProperty?.webPageId ? 'rotate-180' : ''
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UnsavedCompetitorPropertyTable;
